import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, LayoutDashboard, LogOut } from "lucide-react";
import { NAV, logo } from "../config";
import { useAuth } from "../auth/AuthContext";
import { goNav } from "../lib/nav";
import LanguageSwitcher from "./LanguageSwitcher";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleItem = (item) => {
    setOpen(false);
    setTimeout(() => goNav({ navigate, pathname, item, scrollTo }), open ? 250 : 0);
  };

  const handleHash = (href) => {
    setOpen(false);
    setTimeout(() => goNav({ navigate, pathname, item: { href }, scrollTo }), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass border-b border-white/10" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
          <button onClick={() => { setOpen(false); navigate("/"); }} className="flex items-center gap-3 group" data-testid="nav-logo">
            <img src={logo} alt="Arco Orbis2" className="h-11 w-11 object-contain" />
            <span className="notranslate font-display font-medium tracking-[0.25em] text-sm hidden sm:block" translate="no">
              ARCO <span className="gold-text">ORBIS2</span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {NAV.map((item) => (
              <button
                key={item.label}
                onClick={() => handleItem(item)}
                className="text-[13px] font-body text-silver/70 hover:text-gold transition-colors duration-300 relative group"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <button
            onClick={() => { setOpen(false); navigate("/contact"); }}
            className="hidden lg:inline-flex items-center rounded-full border border-gold/40 px-5 py-2 ml-2 text-xs font-body tracking-wide text-gold hover:bg-gold hover:text-black transition-colors duration-300 whitespace-nowrap"
            data-testid="nav-cta"
          >
            Start a Conversation
          </button>

          {/* Auth controls */}
          <div className="hidden lg:flex items-center gap-3 ml-1">
            <LanguageSwitcher />
            {user ? (
              <>
                {user.role === "admin" && (
                  <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-xs font-body text-silver/70 hover:text-gold transition-colors" data-testid="nav-dashboard">
                    <LayoutDashboard size={15} /> Dashboard
                  </button>
                )}
                <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-body text-silver/70 hover:text-gold transition-colors" data-testid="nav-logout" title="Log out">
                  <LogOut size={15} /> {user.name?.split(" ")[0] || "Logout"}
                </button>
              </>
            ) : (
              <button onClick={() => navigate("/login")} className="inline-flex items-center gap-1.5 text-xs font-body text-silver/70 hover:text-gold transition-colors" data-testid="nav-login">
                <User size={15} /> Login
              </button>
            )}
          </div>

          <button
            className="lg:hidden text-silver p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-hamburger"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 glass lg:hidden pt-24 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => handleItem(item)}
                  className="text-left font-display font-light text-3xl py-3 border-b border-white/5 text-white/90 hover:text-gold transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </motion.button>
              ))}
              <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-3">
                <LanguageSwitcher testId="mobile-lang" />
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <button onClick={() => { setOpen(false); setTimeout(() => navigate("/admin"), 200); }} className="text-left font-body text-lg text-gold flex items-center gap-2" data-testid="mobile-nav-dashboard">
                        <LayoutDashboard size={18} /> Dashboard
                      </button>
                    )}
                    <button onClick={() => { setOpen(false); logout(); }} className="text-left font-body text-lg text-silver/80 flex items-center gap-2" data-testid="mobile-nav-logout">
                      <LogOut size={18} /> Logout
                    </button>
                  </>
                ) : (
                  <button onClick={() => { setOpen(false); setTimeout(() => navigate("/login"), 200); }} className="text-left font-body text-lg text-gold flex items-center gap-2" data-testid="mobile-nav-login">
                    <User size={18} /> Login
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { scrollTo };
